import { useRef } from "react";
import { Link } from "react-router-dom";
import { motion, useScroll, useTransform } from "framer-motion";
import {
  AppWindow,
  ArrowRight,
  ArrowUpRight,
  BadgeCheck,
  Clock,
  Droplets,
  FileText,
  Layers,
  MessageCircle,
  PlugZap,
  ShieldCheck,
  Tag,
} from "lucide-react";
import { SEO } from "@/components/SEO";
import { Reveal } from "@/components/Reveal";
import { SectionHeading } from "@/components/SectionHeading";
import { Marquee } from "@/components/Marquee";
import { CTABanner } from "@/components/CTABanner";
import { PineappleIcon } from "@/components/PineappleIcon";
import { IMAGES, PROCESS, SERVICES, STATS } from "@/data/content";
import { waLink } from "@/lib/site";

const SERVICE_ICONS = [Layers, Droplets, Tag, AppWindow, PlugZap];

const MaskedLine = ({ children, delay }) => (
  <span className="block overflow-hidden pb-1">
    <motion.span
      className="block"
      initial={{ y: "112%" }}
      animate={{ y: 0 }}
      transition={{ duration: 0.95, delay, ease: [0.22, 1, 0.36, 1] }}
    >
      {children}
    </motion.span>
  </span>
);

const Hero = () => {
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start start", "end start"] });
  const yImg = useTransform(scrollYProgress, [0, 1], [0, 110]);
  const yText = useTransform(scrollYProgress, [0, 1], [0, -70]);

  return (
    <section ref={ref} className="relative overflow-hidden pb-16 pt-28 sm:pt-36 lg:min-h-screen lg:pb-24" data-testid="hero-section">
      <div className="pointer-events-none absolute -left-32 top-1/3 h-[28rem] w-[28rem] rounded-full bg-gold/10 blur-3xl" aria-hidden="true" />
      <div className="mx-auto grid max-w-7xl items-center gap-14 px-5 sm:px-8 lg:grid-cols-12 lg:gap-8">
        <motion.div style={{ y: yText }} className="lg:col-span-6">
          <motion.div
            initial={{ opacity: 0, y: 16 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.7, delay: 0.1 }}
            className="inline-flex items-center gap-2 rounded-full border border-gold/30 bg-white/70 px-4 py-1.5"
            data-testid="hero-badge"
          >
            <PineappleIcon className="h-4 w-3 text-gold" />
            <span className="font-cinzel text-[10px] font-semibold uppercase tracking-[0.26em] text-gold-dark sm:text-[11px]">
              BTO · Condo · Landed — Singapore
            </span>
          </motion.div>

          <h1 className="mt-7 font-serif text-4xl font-semibold leading-[1.08] tracking-tight text-charcoal sm:text-5xl lg:text-6xl">
            <MaskedLine delay={0.25}>Your new home,</MaskedLine>
            <MaskedLine delay={0.4}>inspected to</MaskedLine>
            <MaskedLine delay={0.55}>
              <em className="text-gold-gradient not-italic sm:italic">perfection.</em>
            </MaskedLine>
          </h1>

          <motion.p
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 0.85 }}
            className="mt-6 max-w-lg text-sm leading-relaxed text-charcoal-soft sm:text-base"
          >
            Independent defect inspections for new BTO flats, condominiums and landed homes across Singapore — so you
            collect your keys with complete peace of mind, and a touch of prosperity.
          </motion.p>

          <motion.div
            initial={{ opacity: 0, y: 18 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.8, delay: 1 }}
            className="mt-8 flex flex-col gap-4 sm:flex-row sm:items-center"
          >
            <a
              href={waLink()}
              target="_blank"
              rel="noopener noreferrer"
              data-testid="hero-whatsapp-cta"
              className="inline-flex items-center justify-center gap-2 rounded-full bg-wa px-7 py-4 text-sm font-bold text-white shadow-wa transition-transform duration-300 hover:scale-[1.04]"
            >
              <MessageCircle className="h-5 w-5" />
              Book on WhatsApp
            </a>
            <Link
              to="/services"
              data-testid="hero-services-link"
              className="inline-flex items-center justify-center gap-2 rounded-full border border-charcoal/20 px-7 py-4 text-sm font-semibold text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
            >
              Explore our services
              <ArrowRight className="h-4 w-4" />
            </Link>
          </motion.div>

          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            transition={{ duration: 0.9, delay: 1.2 }}
            className="mt-10 flex flex-wrap gap-x-7 gap-y-3"
          >
            {[
              { icon: ShieldCheck, label: "Independent & unbiased" },
              { icon: Clock, label: "Report within 24 hours" },
              { icon: BadgeCheck, label: "CONQUAS-informed checks" },
            ].map(({ icon: Icon, label }) => (
              <span key={label} className="flex items-center gap-2 text-xs font-semibold text-charcoal-mute">
                <Icon className="h-4 w-4 text-gold" />
                {label}
              </span>
            ))}
          </motion.div>
        </motion.div>

        <div className="relative lg:col-span-6">
          <motion.div
            style={{ y: yImg }}
            initial={{ opacity: 0, scale: 0.96 }}
            animate={{ opacity: 1, scale: 1 }}
            transition={{ duration: 1.1, delay: 0.5, ease: [0.22, 1, 0.36, 1] }}
            className="relative"
          >
            <div className="gold-ring overflow-hidden rounded-[2rem] shadow-luxury">
              <img
                src={IMAGES.heroMain}
                alt="Elegant living room of a new Singapore home, freshly inspected"
                className="h-[22rem] w-full object-cover sm:h-[30rem]"
                data-testid="hero-image"
              />
            </div>
            <motion.div
              initial={{ opacity: 0, y: 30, rotate: -4 }}
              animate={{ opacity: 1, y: 0, rotate: -4 }}
              transition={{ duration: 0.9, delay: 0.9 }}
              className="absolute -bottom-10 -left-4 hidden w-48 overflow-hidden rounded-2xl border-4 border-ivory shadow-luxury sm:block lg:-left-12 lg:w-56"
            >
              <img src={IMAGES.heroSecondary} alt="Champagne-toned interior detail" className="h-36 w-full object-cover lg:h-40" />
            </motion.div>
            <motion.div
              initial={{ opacity: 0, scale: 0.6 }}
              animate={{ opacity: 1, scale: 1 }}
              transition={{ duration: 0.8, delay: 1.15, type: "spring", stiffness: 160, damping: 16 }}
              className="absolute -right-3 -top-8 flex h-28 w-28 items-center justify-center rounded-full border border-gold/30 bg-ivory shadow-luxury sm:-right-6 sm:h-32 sm:w-32"
              data-testid="hero-crest"
            >
              <svg viewBox="0 0 100 100" className="absolute inset-0 h-full w-full animate-spin-slow" aria-hidden="true">
                <defs>
                  <path id="crest-circle" d="M50,50 m-36,0 a36,36 0 1,1 72,0 a36,36 0 1,1 -72,0" />
                </defs>
                <text className="fill-charcoal" style={{ fontSize: "8.6px", letterSpacing: "2.4px", fontFamily: "Cinzel, serif" }}>
                  <textPath href="#crest-circle">PROSPERITY · PRECISION · PEACE OF MIND ·</textPath>
                </text>
              </svg>
              <PineappleIcon className="h-9 w-7 text-gold" />
            </motion.div>
          </motion.div>
        </div>
      </div>
    </section>
  );
};

const ServicesOverview = () => (
  <section className="px-5 py-20 sm:px-8 sm:py-28" data-testid="services-overview">
    <div className="mx-auto max-w-7xl">
      <SectionHeading
        eyebrow="What We Inspect"
        title="Five pillars of a flawless handover"
        copy="Every Lobang inspection follows one uncompromising scope — nothing is skipped, everything is documented."
      />
      <div className="mt-14 grid gap-6 sm:grid-cols-2 lg:grid-cols-3">
        {SERVICES.map((s, i) => {
          const Icon = SERVICE_ICONS[i];
          return (
            <Reveal key={s.id} delay={i * 0.07}>
              <Link
                to={`/services#${s.id}`}
                data-testid={`service-card-${s.id}`}
                className="group flex h-full flex-col rounded-3xl border border-border bg-white p-7 shadow-luxury transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40"
              >
                <div className="flex items-center justify-between">
                  <span className="flex h-12 w-12 items-center justify-center rounded-2xl bg-cream text-gold-dark transition-colors duration-500 group-hover:bg-gold group-hover:text-white">
                    <Icon className="h-5 w-5" />
                  </span>
                  <span className="font-serif text-sm italic text-gold/70">0{i + 1}</span>
                </div>
                <h3 className="mt-5 font-serif text-xl font-semibold text-charcoal">{s.title}</h3>
                <p className="mt-2 flex-1 text-sm leading-relaxed text-charcoal-soft">{s.tagline} {s.desc.split(".")[0]}.</p>
                <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-gold-dark">
                  Learn more
                  <ArrowUpRight className="h-3.5 w-3.5 transition-transform duration-300 group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                </span>
              </Link>
            </Reveal>
          );
        })}
        <Reveal delay={0.35}>
          <a
            href={waLink()}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="services-overview-cta-card"
            className="group flex h-full flex-col justify-between rounded-3xl bg-charcoal p-7 transition-transform duration-500 hover:-translate-y-1.5"
          >
            <div>
              <PineappleIcon className="h-10 w-8 text-gold" />
              <h3 className="mt-5 font-serif text-xl font-semibold text-ivory">Not sure what your home needs?</h3>
              <p className="mt-2 text-sm leading-relaxed text-ivory/60">
                Send us your floor plan on WhatsApp and we'll recommend the right scope — no obligation.
              </p>
            </div>
            <span className="mt-6 inline-flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gold-light">
              Ask us on WhatsApp
              <ArrowUpRight className="h-3.5 w-3.5" />
            </span>
          </a>
        </Reveal>
      </div>
    </div>
  </section>
);

const Manifesto = () => (
  <section className="relative overflow-hidden bg-charcoal px-5 py-20 sm:px-8 sm:py-28" data-testid="manifesto-section">
    <PineappleIcon className="pointer-events-none absolute -right-10 top-1/2 h-[26rem] w-72 -translate-y-1/2 rotate-12 text-gold/5" strokeWidth={0.8} />
    <div className="mx-auto max-w-5xl">
      <Reveal>
        <p className="eyebrow">Our Belief</p>
        <p className="mt-6 font-serif text-2xl font-medium leading-snug text-ivory sm:text-3xl lg:text-4xl">
          A new home should begin with <em className="text-gold-gradient">certainty</em> — every tile true, every point
          live, every drop of water draining away — and a little <em className="text-gold-gradient">prosperity</em> rolled
          in for good measure.
        </p>
      </Reveal>
      <div className="mt-14 grid grid-cols-2 gap-8 border-t border-ivory/10 pt-10 lg:grid-cols-4">
        {STATS.map((s, i) => (
          <Reveal key={s.label} delay={i * 0.08}>
            <p className="font-serif text-3xl font-semibold text-gold-light sm:text-4xl" data-testid={`stat-value-${i}`}>{s.value}</p>
            <p className="mt-1 text-xs font-semibold uppercase tracking-widest text-ivory/50">{s.label}</p>
          </Reveal>
        ))}
      </div>
    </div>
  </section>
);

const ProcessTeaser = () => (
  <section className="bg-cream px-5 py-20 sm:px-8 sm:py-28" data-testid="process-teaser">
    <div className="mx-auto max-w-7xl">
      <SectionHeading
        eyebrow="The Three-Visit Journey"
        title="From key collection to perfect handover"
        copy="Three deliberate visits, one outcome: a defect-free home, signed off before renovation begins."
      />
      <div className="mt-14 grid gap-6 md:grid-cols-3">
        {PROCESS.map((p, i) => (
          <Reveal key={p.chapter} delay={i * 0.1}>
            <div className="group relative h-full overflow-hidden rounded-3xl border border-border bg-white p-7 shadow-luxury transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40">
              <span className="font-serif text-5xl font-semibold text-sand transition-colors duration-500 group-hover:text-gold/30">
                {p.chapter}
              </span>
              <h3 className="mt-4 font-serif text-xl font-semibold text-charcoal">{p.title}</h3>
              <p className="mt-2 text-sm leading-relaxed text-charcoal-soft">{p.subtitle}. {p.desc.split(".")[0]}.</p>
            </div>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-10 text-center">
        <Link
          to="/process"
          data-testid="process-teaser-link"
          className="inline-flex items-center gap-2 rounded-full border border-charcoal/20 px-7 py-3.5 text-sm font-semibold text-charcoal transition-colors duration-300 hover:border-gold hover:text-gold-dark"
        >
          See the full process
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

const PricingTeaser = () => (
  <section className="px-5 py-20 sm:px-8 sm:py-28" data-testid="pricing-teaser">
    <div className="mx-auto max-w-7xl">
      <SectionHeading
        eyebrow="Transparent Rates"
        title="Simple pricing, no surprises"
        copy="One deep-dive visit, or the full three-trip journey — pick the coverage that fits your handover."
      />
      <div className="mx-auto mt-14 grid max-w-4xl gap-6 sm:grid-cols-2">
        {[
          { label: "BTO Flats", from: "from $108", note: "2-Room, 3-Room, 4-Room, 5-Room & 3Gen flats" },
          { label: "Condominiums", from: "from $178", note: "Priced by floor area · 2,001 sq ft onwards: POA" },
        ].map((c, i) => (
          <Reveal key={c.label} delay={i * 0.1}>
            <Link
              to="/pricing"
              data-testid={`pricing-teaser-${c.label === "BTO Flats" ? "bto" : "condo"}`}
              className="group block h-full rounded-3xl border border-border bg-white p-8 text-center shadow-luxury transition-all duration-500 hover:-translate-y-1.5 hover:border-gold/40"
            >
              <p className="font-cinzel text-xs font-semibold uppercase tracking-[0.28em] text-gold">{c.label}</p>
              <p className="mt-3 font-serif text-4xl font-semibold text-charcoal">{c.from}</p>
              <p className="mt-2 text-sm text-charcoal-mute">{c.note}</p>
              <span className="mt-5 inline-flex items-center gap-1.5 text-xs font-bold uppercase tracking-widest text-gold-dark">
                View full pricing <ArrowUpRight className="h-3.5 w-3.5" />
              </span>
            </Link>
          </Reveal>
        ))}
      </div>
      <Reveal className="mt-6 text-center">
        <p className="text-xs text-charcoal-mute">Landed homes quoted individually · All prices in SGD</p>
      </Reveal>
    </div>
  </section>
);

const StoryTeaser = () => (
  <section className="bg-cream px-5 py-20 sm:px-8 sm:py-28" data-testid="story-teaser">
    <div className="mx-auto grid max-w-7xl items-center gap-12 lg:grid-cols-2">
      <Reveal>
        <div className="gold-ring overflow-hidden rounded-[2rem] shadow-luxury">
          <img src={IMAGES.hdb} alt="Colourful HDB blocks in Singapore" className="h-80 w-full object-cover sm:h-96" loading="lazy" />
        </div>
      </Reveal>
      <Reveal delay={0.12}>
        <p className="eyebrow">Why 'Lobang'?</p>
        <h2 className="mt-4 font-serif text-3xl font-semibold leading-tight tracking-tight text-charcoal sm:text-4xl">
          A good lobang, rolled in like a pineapple
        </h2>
        <p className="mt-5 text-sm leading-relaxed text-charcoal-soft sm:text-base">
          In Singlish, a <em>lobang</em> is a valuable opportunity — an insider connection you pass to a friend. In
          Hokkien, the pineapple is <em>Ong Lai</em> (旺来): prosperity arriving at your door. We named ourselves after
          both, because a thorough defect inspection is the best lobang a new homeowner can get.
        </p>
        <Link
          to="/about"
          data-testid="story-teaser-link"
          className="mt-7 inline-flex items-center gap-2 text-sm font-bold text-gold-dark transition-colors hover:text-gold"
        >
          Read our story
          <ArrowRight className="h-4 w-4" />
        </Link>
      </Reveal>
    </div>
  </section>
);

export default function Home() {
  return (
    <>
      <SEO
        title="Lobang Home Inspection | BTO, Condo & Landed Defect Checks Singapore"
        description="Independent defect inspections for new BTO HDB flats, condos and landed homes in Singapore. Hollow-tile tapping, ponding tests, blue-tape marking & electrical checks. WhatsApp +65 8490 0316."
      />
      <Hero />
      <Marquee />
      <ServicesOverview />
      <Manifesto />
      <ProcessTeaser />
      <PricingTeaser />
      <section className="px-5 py-16 sm:px-8 sm:py-24" aria-labelledby="home-details-title">
        <div className="mx-auto max-w-7xl">
          <p className="eyebrow">Every corner matters</p>
          <h2 id="home-details-title" className="mt-4 font-serif text-3xl font-semibold sm:text-4xl">A fresh start, down to the details</h2>
          <p className="mt-4 max-w-2xl leading-relaxed text-charcoal-soft">From BTO flats to condos and landed homes in Singapore, we check the everyday spaces that make a house your home.</p>
          <div className="mt-8 grid gap-6 sm:grid-cols-3">
            {[SERVICES[0], SERVICES[1], SERVICES[3]].map((service) => (
              <figure key={service.title} className="overflow-hidden rounded-3xl border border-border bg-cream">
                <img src={service.image} alt={service.title + " — illustrative home detail"} loading="lazy" className="h-64 w-full object-cover" />
                <figcaption className="p-5 text-base font-semibold text-charcoal">{service.title}</figcaption>
              </figure>
            ))}
          </div>
          <p className="mt-3 text-sm text-charcoal-soft">Interior inspiration · illustrative photography</p>
        </div>
      </section>
      <StoryTeaser />
      <CTABanner />
    </>
  );
}
